import React from 'react';
import { motion } from 'motion/react';
import type { BookingCategoryClass } from '@/features/booking/types/bookingTypes';
import { getTicketCategoryStyle, isPremiumCategory } from './TicketCardStyles';
import { getTimezoneAbbreviation } from '@/lib/utils';

export type TierRibbonVariant = 'date-tier' | 'ticket-center' | 'separator';

interface TierRibbonProps {
  /** Booking category class for tier styling */
  tierClass?: BookingCategoryClass;
  /** Layout variant */
  variant?: TierRibbonVariant;
  /** Event start time ISO string (for date-tier variant) */
  startTime?: string;
  /** Timezone ID for formatting */
  timezoneId?: string;
  /** Text suffix - "TIER" or "TICKET" */
  textSuffix?: 'TIER' | 'TICKET';
  /** Optional className */
  className?: string;
  /** Whether the ribbon background spans the full width of the screen */
  fullWidth?: boolean;
}

/**
 * Minimal, elegant tier ribbon for displaying booking tier information
 */
export const TierRibbon: React.FC<TierRibbonProps> = ({
  tierClass = 'Standard',
  variant = 'date-tier',
  startTime,
  timezoneId,
  textSuffix = 'TIER',
  className = '',
  fullWidth = false,
}) => {
  // Get tier styling from API data
  const style = getTicketCategoryStyle(tierClass);
  const isPremium = isPremiumCategory(tierClass);
  const isStandard = tierClass === 'Standard';

  // For Standard tier, don't render separator/ticket-center variants at all
  if (isStandard && variant !== 'date-tier') {
    return null;
  }

  // Format date
  const formatDateInfo = () => {
    if (!startTime || !timezoneId) return null;
    const date = new Date(startTime);
    const tzAbbr = getTimezoneAbbreviation(startTime, timezoneId);
    return {
      day: date.toLocaleDateString('en-US', { weekday: 'short', timeZone: timezoneId }).toUpperCase(),
      date: date.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric', timeZone: timezoneId }),
      time: `${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: timezoneId })} ${tzAbbr}`,
    };
  };

  const dateInfo = formatDateInfo();
  const tierName = tierClass === 'Standard' ? '' : tierClass.toUpperCase();
  const tierText = tierName ? `${tierName} ${textSuffix}` : '';

  // Small inline badge (matching TicketCard sizing exactly)
  const Badge = ({ size = 'sm' }: { size?: 'sm' | 'xs' }) => {
    if (!style.badgeSvg || !isPremium) return null;
    const sizeClass = size === 'sm' ? 'w-3.5 h-3.5' : 'w-3 h-3';
    return (
      <div
        className={`${sizeClass} flex-shrink-0 flex items-center justify-center`}
        style={{ color: style.accentColor }}
        dangerouslySetInnerHTML={{ __html: style.badgeSvg }}
      />
    );
  };

  // Tier label with badges
  const TierLabel = ({ size = 'sm' }: { size?: 'sm' | 'xs' }) => {
    if (!tierText) return null;
    const textClass = size === 'sm' 
      ? 'text-xs font-bold tracking-widest' 
      : 'text-[10px] font-bold tracking-wider';
    
    return (
      <div className="flex items-center gap-1.5">
        <Badge size={size} />
        <span className={textClass} style={{ color: style.accentColor }}>
          {tierText}
        </span>
        <Badge size={size} />
      </div>
    );
  };

  // Variant-specific styles - all use subtle, transparent styling
  const getVariantStyles = () => {
    // Use subtle transparent styling for all variants to blend with parent containers
    // Tier indication comes from accent colors and badges, not heavy backgrounds
    return {
      background: `linear-gradient(90deg, ${style.borderColor}15, ${style.borderColor}50, ${style.borderColor}15)`,
      borderTop: `1px solid ${style.borderColor}60`,
      borderBottom: `1px solid ${style.borderColor}60`,
    };
  };

  return (
    <motion.div
      className={`relative w-full ${className}`}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >

      {/* DATE-TIER VARIANT: Date on left, tier on right */}
      {variant === 'date-tier' && (
        <div 
          className={`py-3 relative ${fullWidth ? 'w-full' : 'px-6'}`}
          style={getVariantStyles()}
        >
          <div className={fullWidth ? "w-full mx-auto px-6 md:px-8 lg:px-12 xl:px-20 2xl:px-[12vw] flex items-center justify-between" : "flex items-center justify-between w-full"}>
            {/* Left: Date & Time */}
            {dateInfo && (
              <div className="flex flex-wrap items-center gap-2 sm:gap-4">
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke={style.accentColor} viewBox="0 0 24 24" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
                  </svg>
                  <span className="text-base sm:text-lg lg:text-xl font-semibold whitespace-nowrap" style={{ color: style.textColor }}>
                    {dateInfo.day}, {dateInfo.date}
                  </span>
                </div>
                <span className="hidden sm:inline text-sm opacity-50" style={{ color: style.secondaryTextColor }}>•</span>
                <span className="text-base sm:text-lg lg:text-xl font-medium" style={{ color: style.textColor }}>
                  {dateInfo.time}
                </span>
              </div>
            )}

            {/* Right: Tier */}
            <TierLabel size="sm" />
          </div>

          {/* Shimmer effect - only for premium tiers */}
          {isPremium && (
            <motion.div
              className="absolute inset-0 pointer-events-none overflow-hidden"
            >
              <motion.div
                className="absolute inset-0"
                style={{
                  background: `linear-gradient(90deg, transparent 0%, ${style.accentColor}20 50%, transparent 100%)`,
                  width: '200%',
                }}
                animate={{ x: ['-100%', '100%'] }}
                transition={{ duration: 3, ease: 'linear', repeat: Infinity }}
              />
            </motion.div>
          )}
        </div>
      )}

      {/* TICKET-CENTER VARIANT: Centered tier label */}
      {variant === 'ticket-center' && (
        <div 
          className="flex items-center justify-center px-4 py-2"
          style={getVariantStyles()}
        >
          <TierLabel size="sm" />

          {/* Shimmer effect */}
          <motion.div
            className="absolute inset-0 pointer-events-none overflow-hidden"
          >
            <motion.div
              className="absolute inset-0"
              style={{
                background: `linear-gradient(90deg, transparent 0%, ${style.accentColor}20 50%, transparent 100%)`,
                width: '200%',
              }}
              animate={{ x: ['-100%', '100%'] }}
              transition={{ duration: 3, ease: 'linear', repeat: Infinity }}
            />
          </motion.div>
        </div>
      )}

      {/* SEPARATOR VARIANT: Compact centered tier */}
      {variant === 'separator' && (
        <div 
          className="flex items-center justify-center px-3 py-1.5"
          style={getVariantStyles()}
        >
          <TierLabel size="xs" />

          {/* Shimmer effect */}
          <motion.div
            className="absolute inset-0 pointer-events-none overflow-hidden"
          >
            <motion.div
              className="absolute inset-0"
              style={{
                background: `linear-gradient(90deg, transparent 0%, ${style.accentColor}18 50%, transparent 100%)`,
                width: '200%',
              }}
              animate={{ x: ['-100%', '100%'] }}
              transition={{ duration: 3, ease: 'linear', repeat: Infinity }}
            />
          </motion.div>
        </div>
      )}
    </motion.div>
  );
};

export default TierRibbon;
